/**
 * ChoirCheckIn - Public QR landing for choir attendance.
 * Members enter phone + password to mark present.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, X, CalendarX, Music, Clock, Phone, Lock, Eye, EyeOff, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const BUILT_BY = 'Oluwakorede Akinsanya';

export default function ChoirCheckIn() {
  const [loading, setLoading] = useState(true);
  const [activeEvent, setActiveEvent] = useState(null);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle | requesting | error

  // ?venue=TOKEN means "scanned at the church QR" — bypasses GPS check on the backend
  const venueToken = (() => {
    try {
      return new URLSearchParams(window.location.search).get('venue') || null;
    } catch { return null; }
  })();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/choir/events/active`);
        setActiveEvent(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const getLocation = () => new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Your device does not support GPS. Please use a phone with location services.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (err) => {
        const msg = err.code === err.PERMISSION_DENIED
          ? 'Location permission denied. Please allow location access in your browser settings and refresh.'
          : err.code === err.POSITION_UNAVAILABLE
          ? 'Could not determine your location. Move closer to a window or step outside, then try again.'
          : err.code === err.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Could not get your location. Please ensure GPS is enabled.';
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  });

  const submit = async () => {
    if (!phone.trim() || !password) return;
    try {
      setSubmitting(true);
      const payload = { phone, password };
      if (venueToken) {
        // Scanned the on-site QR → backend trusts this as proof of presence
        payload.venue_token = venueToken;
      } else {
        // No venue token → must verify physical presence via GPS
        setLocationStatus('requesting');
        const coords = await getLocation();
        setLocationStatus('idle');
        payload.lat = coords.lat;
        payload.lng = coords.lng;
        payload.accuracy = coords.accuracy;
      }
      const res = await axios.post(`${API}/choir/attendance/scan`, payload);
      setResult(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Scan failed.';
      setLocationStatus('error');
      setResult({ status: 'error', message: msg });
    }
    setSubmitting(false);
  };

  if (loading) return <Shell><div className="text-amber-300 text-lg font-medium animate-pulse">Loading…</div></Shell>;

  // No active event today
  if (!activeEvent?.active) {
    const upcoming = activeEvent?.upcoming_today || [];
    const next = upcoming[0];
    return (
      <Shell>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl border-2 border-amber-300 text-center">
          <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <CalendarX className="w-10 h-10 text-amber-600" />
          </div>
          {next ? (
            <>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-800 to-purple-600 bg-clip-text text-transparent">
                Scan Window Not Open Yet
              </h1>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mt-5 text-left">
                <p className="text-xs uppercase tracking-wider text-violet-500 font-bold">Next event today</p>
                <p className="text-lg font-bold text-violet-900 mt-1">{next.title || 'Choir Event'}</p>
                <p className="text-sm text-slate-600">Starts at <strong>{next.start_time}</strong> (UK time)</p>
                <div className="mt-3 flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">Scan opens at <strong>{next.window_opens_at}</strong></span>
                </div>
              </div>
              <p className="text-slate-500 text-xs mt-4">
                Server time now: {activeEvent?.now} (Europe/London)
              </p>
              {upcoming.length > 1 && (
                <p className="text-slate-500 text-xs mt-1">
                  +{upcoming.length - 1} more event{upcoming.length - 1 === 1 ? '' : 's'} later today
                </p>
              )}
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-800 to-purple-600 bg-clip-text text-transparent">No Event Today</h1>
              <p className="text-slate-600 mt-3 leading-relaxed">
                Choir attendance is only taken on <strong>Sunday services</strong> and <strong>scheduled rehearsals</strong>.
              </p>
              <p className="text-slate-500 text-sm mt-3">Please check with your choir leader for the next scheduled event.</p>
            </>
          )}
        </motion.div>
      </Shell>
    );
  }

  // Result screen
  if (result) {
    const isSuccess = result.status === 'marked_present' || result.status === 'already_present';
    return (
      <Shell>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl border-2 border-amber-300 text-center">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-5 ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
            {isSuccess ? <Check className="w-14 h-14 text-green-600" /> : <X className="w-14 h-14 text-red-600" />}
          </div>
          <h1 className={`text-2xl font-bold ${isSuccess ? 'text-green-700' : 'text-red-700'}`}>
            {result.status === 'marked_present' ? 'You Are Present!' : result.status === 'already_present' ? 'Already Recorded' : 'Could Not Verify'}
          </h1>
          <p className="text-slate-700 mt-3 leading-relaxed">{result.message}</p>
          {result.time_of_arrival && (
            <div className="bg-violet-50 rounded-xl p-4 mt-5 border border-violet-200">
              <div className="flex items-center justify-center gap-2 text-violet-800">
                <Clock className="w-5 h-5" />
                <span className="font-mono font-bold text-lg">{result.time_of_arrival}</span>
              </div>
              <p className="text-xs text-violet-600 mt-1">Your time of arrival</p>
            </div>
          )}
          <button
            onClick={() => { setResult(null); setPhone(''); setPassword(''); }}
            className="mt-6 w-full py-3 rounded-xl bg-violet-100 hover:bg-violet-200 text-violet-800 font-medium"
            data-testid="choir-scan-done-btn"
          >
            Close
          </button>
        </motion.div>
      </Shell>
    );
  }

  // Active event — show scan form
  const ev = activeEvent.event;
  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl border-2 border-amber-300">
        <div className="bg-gradient-to-r from-violet-800 via-purple-700 to-violet-800 text-white rounded-2xl p-4 mb-5 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
          <div className="relative flex items-center gap-3">
            <Music className="w-8 h-8 text-amber-300" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-amber-200 font-bold">Active Now</p>
              <h2 className="font-bold text-lg">{ev.title}</h2>
              <p className="text-xs text-amber-100/80">{ev.date} • Started {ev.start_time}</p>
            </div>
          </div>
        </div>

        <h3 className="text-center text-slate-700 font-medium mb-4">Mark yourself present</h3>

        <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
        <div className="relative mb-4">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="07xxx xxxxxx"
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-violet-200 focus:border-amber-400 outline-none"
            data-testid="choir-scan-phone"
          />
        </div>

        <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
        <div className="relative mb-5">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Your password"
            onKeyPress={e => e.key === 'Enter' && submit()}
            className="w-full pl-10 pr-10 py-3 rounded-xl border-2 border-violet-200 focus:border-amber-400 outline-none"
            data-testid="choir-scan-password"
          />
          <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <button onClick={submit} disabled={submitting || !phone.trim() || !password}
          className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 font-bold shadow-lg shadow-amber-500/30 hover:from-amber-400 disabled:opacity-50 transition-all"
          data-testid="choir-scan-submit">
          {locationStatus === 'requesting' ? 'Checking your location…' : submitting ? 'Marking present…' : 'Mark Present'}
        </button>

        {venueToken ? (
          <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-2 px-3">
            <Check className="w-3.5 h-3.5" />
            <span><strong>On-site QR detected</strong> — no GPS required</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-slate-500">
            <MapPin className="w-3.5 h-3.5" />
            <span>You must be at the church · GPS will be checked</span>
          </div>
        )}

        <p className="text-center text-xs text-slate-500 mt-2">
          Use the <strong>Master Password</strong> emailed to you by the Main Admin.
        </p>
      </motion.div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl -translate-y-48 translate-x-48"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-400/15 rounded-full blur-3xl translate-y-48 -translate-x-48"></div>
      <img src="/church-logo.png?v=2" alt="" className="absolute inset-0 m-auto w-[70vh] object-contain pointer-events-none opacity-[0.05]" />

      <div className="relative z-10 w-full flex flex-col items-center">
        <div className="flex items-center gap-3 mb-6">
          <img src="/images/church-logo.png?v=2" alt="CCC VOB" className="w-14 h-14" />
          <div>
            <h1 className="text-amber-300 font-bold text-sm uppercase tracking-widest">CCC VOB Parish</h1>
            <p className="text-amber-100/70 text-xs">Choir Attendance</p>
          </div>
        </div>
        {children}
        <p className="text-amber-200/40 text-[10px] mt-6">Built by {BUILT_BY}</p>
      </div>
    </div>
  );
}
